import { createElement } from 'react';
import { z } from 'zod';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    type CaregiverListWidgetModel,
    caregiverListWidgetModelSchema,
    type CaregiverListWidgetRow,
    type DynamicWidgetIssue,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    addRetrieveEnvelopeBindings,
    buildRetrieveArtifact,
    matchesRetrieveDomain,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_adapter';
import { RetrieveArtifactController } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_controller';
import { CaregiverListWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_caregiver_list_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const DOMAIN = 'caregivers';

const caregiverSourceRowSchema = z.object({
    caregiver_id: z.string().min(1),
    display_name: z.string().min(1),
    active: z.boolean(),
    roles: z.array(z.string()),
    office: z.string().nullable(),
    city: z.string().nullable(),
    qualification_tags: z.array(z.string()),
});

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function stringArray(value: unknown): Array<string> {
    return Array.isArray(value) &&
        value.every((item): item is string => typeof item === 'string')
        ? value
        : [];
}

const EMPTY_RANKED_FIELDS = {
    rank: null,
    shiftId: null,
    distanceMiles: null,
    weeklyHours: null,
    desiredWeeklyHours: null,
    previousVisits: null,
    available: null,
    preferred: false,
    warnings: [],
} as const;

function mapCaregiverRow(rawRow: unknown): CaregiverListWidgetRow {
    const parsed = caregiverSourceRowSchema.safeParse(rawRow);
    if (parsed.success) {
        return {
            caregiverId: parsed.data.caregiver_id,
            displayName: parsed.data.display_name,
            active: parsed.data.active,
            roles: parsed.data.roles,
            office: parsed.data.office,
            city: parsed.data.city,
            qualificationTags: parsed.data.qualification_tags,
            valid: true,
            ...EMPTY_RANKED_FIELDS,
        };
    }
    const row = isRecord(rawRow) ? rawRow : {};
    return {
        caregiverId: optionalString(row['caregiver_id']),
        displayName: optionalString(row['display_name']),
        active: typeof row['active'] === 'boolean' ? row['active'] : null,
        roles: stringArray(row['roles']),
        office: optionalString(row['office']),
        city: optionalString(row['city']),
        qualificationTags: stringArray(row['qualification_tags']),
        valid: false,
        ...EMPTY_RANKED_FIELDS,
    };
}

export function hydrateRetrieveCaregiverListWidgetModel(
    model: CaregiverListWidgetModel,
    rawRows: ReadonlyArray<unknown>,
    provenance: 'event_preview' | 'resource',
): CaregiverListWidgetModel {
    const sourceKind =
        provenance === 'event_preview' ? 'event_source' : 'resource_source';
    const source = model.sources.find(
        (candidate) => candidate.kind === sourceKind,
    );
    const sourceId =
        source === undefined ? null : getDynamicWidgetSourceId(source);
    const rows = rawRows.map(mapCaregiverRow);
    const rowIssues: Array<DynamicWidgetIssue> = rows.flatMap(
        (row, rowIndex) =>
            row.valid
                ? []
                : [
                      {
                          id: `${model.widgetId}.caregiver-row-${rowIndex}`,
                          code: 'source_invalid' as const,
                          level: 'warning' as const,
                          scope: 'section' as const,
                          bindingIds: [
                              provenance === 'event_preview'
                                  ? 'previewRows'
                                  : 'resourceRows',
                          ],
                          sourceIds: sourceId === null ? [] : [sourceId],
                          retryable: false,
                          safeDetails: { rowIndex },
                      },
                  ],
    );
    return caregiverListWidgetModelSchema.parse({
        ...model,
        rows,
        rowProvenance: provenance,
        issues: [
            ...model.issues.filter(
                (issue) =>
                    !issue.id.startsWith(`${model.widgetId}.caregiver-row-`),
            ),
            ...rowIssues,
        ],
    });
}

export function buildRetrieveCaregiverListWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): CaregiverListWidgetModel | null {
    const artifact = buildRetrieveArtifact(entry, context, DOMAIN);
    if (artifact === null || !entry.toolCallEventId || !entry.runId) {
        return null;
    }
    addRetrieveEnvelopeBindings({ artifact });
    artifact.builder.addCalculatedField({
        id: 'preview.rows.fields',
        sourceIds: [artifact.eventSourceId],
        paths: [
            '/head_preview/*/caregiver_id',
            '/head_preview/*/display_name',
            '/head_preview/*/active',
            '/head_preview/*/roles',
            '/head_preview/*/office',
            '/head_preview/*/city',
            '/head_preview/*/qualification_tags',
        ],
        transform: 'mapRetrieveCaregiverPreviewRows',
        rawValue: artifact.envelope.head_preview,
        value: null,
        renderedValue: null,
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'fields',
        }),
    });
    artifact.builder.addLiveField({
        id: 'resource.rows.fields',
        sourceIds: [artifact.resourceSourceId],
        paths: [
            '/rows/*/caregiver_id',
            '/rows/*/display_name',
            '/rows/*/active',
            '/rows/*/roles',
            '/rows/*/office',
            '/rows/*/city',
            '/rows/*/qualification_tags',
        ],
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'fields',
        }),
    });
    const receiptModel = caregiverListWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'entities'),
            templateVersion: 1,
            issues: [],
        }),
        template: 'entities',
        templateVersion: 1,
        rows: [],
        rowCount: artifact.envelope.row_count,
        rowProvenance: 'event_preview',
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
    });
    return hydrateRetrieveCaregiverListWidgetModel(
        receiptModel,
        artifact.envelope.head_preview,
        'event_preview',
    );
}

export const retrieveCaregiverListAdapter: DynamicWidgetAdapter<CaregiverListWidgetModel> =
    {
        template: 'entities',
        templateVersion: 1,
        toolNames: new Set(['retrieve', 'retrieve_caregivers']),
        matches: (entry) => matchesRetrieveDomain(entry, DOMAIN),
        build: buildRetrieveCaregiverListWidgetModel,
        render(model, handlers) {
            return createElement(
                RetrieveArtifactController<CaregiverListWidgetModel>,
                {
                    model,
                    hydrateRows: (previewModel, rows) =>
                        hydrateRetrieveCaregiverListWidgetModel(
                            previewModel,
                            rows,
                            'resource',
                        ),
                    render: ({ model: completeModel, loadError }) =>
                        createElement(CaregiverListWidget, {
                            model: completeModel,
                            loadError,
                            onCaregiverClick: handlers.onCaregiverClick,
                        }),
                },
            );
        },
    };
